import {
  adjustFromTimeZone,
  adjustToTimeZone,
  type RrcLocationProfile,
  type Address,
} from '@ots-frontend/ots-data';
import {
  rrcProfileOptions,
  rrcProfileCodeLabel,
  rrcProfileDate,
} from '../rrc-profile-ui';

export const locationProfileOptions = rrcProfileOptions;
export const locationProfileCodeLabel = rrcProfileCodeLabel;
export const locationProfileDate = rrcProfileDate;
export type LocationProfileDraft = {
  locationId: string;
  locationType: string;
  rrcDistrict: string;
  facilityName: string;
  facilityPermitNumber: string;
  leaseName: string;
  leaseNumber: string;
  gasPlantNumber: string;
  latCoord: string;
  longCoord: string;
  start?: number;
  end?: number;
  commercial: string;
  contactName: string;
  contactPhone: string;
  contactEmail: string;
  address: Address;
  autoEp9SignerId: string;
  autoEp9SignerStrategy: string;
  autoEp10SignerId: string;
  autoEp10SignerStrategy: string;
};
export type LocationProfileErrors = Partial<
  Record<keyof LocationProfileDraft, string>
>;
export const toLocationProfileDraft = (
  profile?: Partial<RrcLocationProfile>
): LocationProfileDraft => ({
  locationId: profile?.locationId ?? '',
  locationType: profile?.locationType ?? 'SITE',
  rrcDistrict: profile?.rrcDistrict ?? '',
  facilityName: profile?.facilityName ?? '',
  facilityPermitNumber: profile?.facilityPermitNumber ?? '',
  leaseName: profile?.leaseName ?? '',
  leaseNumber: profile?.leaseNumber ?? '',
  gasPlantNumber: profile?.gasPlantNumber ?? '',
  latCoord: profile?.latCoord == null ? '' : String(profile.latCoord),
  longCoord: profile?.longCoord == null ? '' : String(profile.longCoord),
  start:
    profile?.effectiveStartDttm == null
      ? undefined
      : adjustFromTimeZone(profile.effectiveStartDttm).getTime(),
  end:
    profile?.effectiveEndDttm == null
      ? undefined
      : adjustFromTimeZone(profile.effectiveEndDttm).getTime(),
  commercial:
    profile?.commercial == null ? 'unknown' : profile.commercial ? 'yes' : 'no',
  contactName: profile?.contactName ?? '',
  contactPhone: profile?.contactPhone ?? '',
  contactEmail: profile?.contactEmail ?? '',
  address: { ...profile?.address, country: profile?.address?.country ?? 'US' },
  autoEp9SignerId: profile?.autoEp9SignerId ?? '',
  autoEp9SignerStrategy: profile?.autoEp9SignerStrategy ?? '',
  autoEp10SignerId: profile?.autoEp10SignerId ?? '',
  autoEp10SignerStrategy: profile?.autoEp10SignerStrategy ?? '',
});
export const validateLocationProfile = (
  draft: LocationProfileDraft
): LocationProfileErrors => {
  const errors: LocationProfileErrors = {};
  for (const key of ['locationId', 'rrcDistrict', 'facilityName'] as const)
    if (!draft[key].trim()) errors[key] = 'Required';
  if (!['SITE', 'WELL'].includes(draft.locationType))
    errors.locationType = 'Choose a site or well.';
  if (draft.start == null || !Number.isFinite(draft.start))
    errors.start = 'An effective start date is required.';
  if (
    draft.end != null &&
    (!Number.isFinite(draft.end) ||
      (draft.start != null && draft.end <= draft.start))
  )
    errors.end = 'The end must be after the start.';
  if (
    draft.contactEmail &&
    !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(draft.contactEmail.trim())
  )
    errors.contactEmail = 'Enter a valid email address.';
  const lat = draft.latCoord.trim(),
    lon = draft.longCoord.trim();
  if (lat || lon) {
    if (!lat || !Number.isFinite(Number(lat)) || Math.abs(Number(lat)) > 90)
      errors.latCoord = 'Enter a latitude between -90 and 90.';
    if (!lon || !Number.isFinite(Number(lon)) || Math.abs(Number(lon)) > 180)
      errors.longCoord = 'Enter a longitude between -180 and 180.';
  }
  for (const form of ['autoEp9', 'autoEp10'] as const)
    if (
      draft[`${form}SignerStrategy`] === 'SELECTED' &&
      !draft[`${form}SignerId`]
    )
      errors[`${form}SignerId`] = 'Choose a signer for the Selected strategy.';
  return errors;
};
const optional = (value: string) => value.trim() || undefined;
/** Only editable DTO fields leave the form; copied snapshots and audit identities do not. */
export const toLocationProfilePayload = (
  draft: LocationProfileDraft,
  id?: string
): Partial<RrcLocationProfile> => ({
  ...(id ? { id } : {}),
  locationId: draft.locationId,
  locationType: draft.locationType,
  rrcDistrict: draft.rrcDistrict,
  facilityName: draft.facilityName.trim(),
  facilityPermitNumber: optional(draft.facilityPermitNumber),
  leaseName: optional(draft.leaseName),
  leaseNumber: optional(draft.leaseNumber),
  gasPlantNumber: optional(draft.gasPlantNumber),
  latCoord: draft.latCoord.trim() ? Number(draft.latCoord) : undefined,
  longCoord: draft.longCoord.trim() ? Number(draft.longCoord) : undefined,
  effectiveStartDttm:
    draft.start == null ? undefined : adjustToTimeZone(draft.start).getTime(),
  effectiveEndDttm:
    draft.end == null ? undefined : adjustToTimeZone(draft.end).getTime(),
  commercial:
    draft.commercial === 'unknown' ? undefined : draft.commercial === 'yes',
  contactName: optional(draft.contactName),
  contactPhone: optional(draft.contactPhone),
  contactEmail: optional(draft.contactEmail),
  address: Object.fromEntries(
    Object.entries(draft.address).map(([key, value]) => [
      key,
      value?.trim() || undefined,
    ])
  ),
  autoEp9SignerStrategy: optional(draft.autoEp9SignerStrategy),
  autoEp9SignerId:
    draft.autoEp9SignerStrategy === 'SELECTED'
      ? optional(draft.autoEp9SignerId)
      : undefined,
  autoEp10SignerStrategy: optional(draft.autoEp10SignerStrategy),
  autoEp10SignerId:
    draft.autoEp10SignerStrategy === 'SELECTED'
      ? optional(draft.autoEp10SignerId)
      : undefined,
});
